import { Box, Stack, Text, useColorModeValue, Icon, Tooltip, useBreakpointValue } from "@chakra-ui/react";
import React from "react";
import { FiExternalLink } from "react-icons/fi";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import type { Invoice } from "types/billing/UserInvoicesResponse";

interface InvoiceItemProps {
  invoice: Invoice;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const getStatusColor = (status: string, tagColorValue: string) => {
  switch (status.toLowerCase()) {
    case "paid":
      return `green.${tagColorValue}`;
    case "pending":
      return `yellow.${tagColorValue}`;
    case "overdue":
      return `red.${tagColorValue}`;
    case "cancelled":
      return `gray.${tagColorValue}`;
    default:
      return `blue.${tagColorValue}`;
  }
};

export const InvoiceItem: React.FC<InvoiceItemProps> = ({ invoice }) => {
  const buttonColor = useColorModeValue("primary.default", "primary.default");
  const tagColorValue = useColorModeValue("600", "600");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });

  return (
    <Box
      py="1rem"
      _hover={{ bg: useColorModeValue("gray.50", "gray.800") }}
      onClick={() => invoice.url && window.open(invoice.url, "_blank")}>
      <Stack direction={isMobile ? "column" : "row"} justify="space-between" align={isMobile ? "start" : "center"} spacing={2}>
        <Stack direction="column" spacing={1}>
          {invoice.lineItems.length > 0 && <TextOverflowTooltip label={invoice.lineItems.join(", ")} fontSize="sm" color={textColor} />}
          <TextOverflowTooltip label={`Invoice ${invoice.id}`} style={{ fontWeight: 500 }} fontSize="xs" color={subTextColor} />
        </Stack>
        <Stack direction="row" spacing={"1.5rem"} align="center">
          <Stack direction="column" spacing={1} align="end">
            <Stack direction="column" spacing={2} align="end">
              <Box fontSize={"11px"} borderRadius={"md"} fontWeight={"bold"} color={getStatusColor(invoice.status, tagColorValue)}>
                {invoice.status.toUpperCase()}
              </Box>
              <Text fontSize="xs" fontWeight="medium" color={textColor}>
                {invoice.amount}
              </Text>
            </Stack>
            <Text fontSize="11px" color={subTextColor}>
              {formatDate(invoice.date)}
            </Text>
          </Stack>
          <Tooltip label={invoice.url ? "View invoice" : ""} placement="top">
            <Box>
              <Icon
                onClick={(e) => {
                  e.preventDefault();
                  invoice.url && window.open(invoice.url, "_blank");
                }}
                _hover={{}}
                _active={{}}
                aria-label="View invoice"
                as={FiExternalLink}
                opacity={invoice.url ? 1 : 0}
                color={buttonColor}
                fontSize={"1.3rem"}
              />
            </Box>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};
